// @ts-nocheck
import { useEffect, useState } from 'react';
import { Plus, Trash2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { getAiAgentDescriptionCharLimit } from '@/utilities/aiAgentAcl';
import { Checkbox } from '@/components/ui/checkbox';
import HeadingInfo from './HeadingInfo';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { parseCurl, looksLikeCurl, guessParamType } from './curlParser';

const { API_PARAMS_MAX_ITEMS } = AI_AGENT_FIELD_LIMITS;

// ---- Limits (as per spec) ----
const FN_NAME_MAX = 50;
const PARAM_NAME_MAX = 50;
const HEADERS_MAX_ITEMS = 10;
const HEADER_KEY_MAX = 50;
const HEADER_VALUE_MAX = 500;
const URL_MAX = 500;
const TIMEOUT_MIN = 1;
const TIMEOUT_MAX = 30;
const RETRY_MIN = 0;
const RETRY_MAX = 3;

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const PARAM_TYPES = ['string', 'number', 'boolean', 'date'];
const PARAM_LOCATIONS = [
  { value: 'path', label: 'Path' },
  { value: 'query', label: 'Query' },
  { value: 'body', label: 'Body' },
  { value: 'header', label: 'Header' },
];
const AUTH_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'api_key', label: 'API Key' },
  { value: 'basic', label: 'Basic Auth' },
];

// ---- Info texts ----
const INFO = {
  fnName: `A unique identifier for this API. Example: get_order_status.`,
  paramName: `The name of an input this API needs. Example: order_id.`,
  type: 'The kind of value this param takes. ',
  location: 'Where the param goes in the request.',
  required: 'When on, the agent must have this value before calling the API. .',
  paramDesc: (limit: number) =>
    `Tells the agent what value to pass. Example: "The order ID the caller gave, e.g. ORD-10432." `,
  headers: `Extra key-value pairs sent with every request. Example: Accept: application/json. `,
  auth: 'How the request authenticates with the API. ',
  timeout: `Seconds to wait for a response before giving up. `,
  retry: `How many times to retry a failed call. `,
};

const genId = () =>
  (crypto.randomUUID?.() as string) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const emptyParam = () => ({
  id: genId(),
  name: '',
  type: 'string',
  location: 'query',
  description: '',
  required: false,
});

const emptyHeader = () => ({ id: genId(), key: '', value: '' });

const emptyForm = () => ({
  name: '',
  description: '',
  method: 'GET',
  url: '',
  parameters: [],
  headers: [],
  authentication: 'none',
  authValue: '',
  successPath: '',
  errorPath: '',
  timeout: '10',
  retryAttempts: '1',
  backoffFactor: '2',
});


const normalizeType = (t: string) => {
  const v = String(t || '').toLowerCase();
  if (PARAM_TYPES.includes(v)) return v;
  if (v === 'integer') return 'number';
  return 'string';
};
const normalizeLocation = (l: string) => {
  const v = String(l || '').toLowerCase();
  return PARAM_LOCATIONS.some((x) => x.value === v) ? v : 'query';
};
const normalizeAuth = (a: string) => {
  const map: any = {
    none: 'none',
    'api key': 'api_key',
    api_key: 'api_key',
    'bearer token': 'bearer',
    bearer: 'bearer',
    'basic auth': 'basic',
    basic: 'basic',
  };
  return map[String(a || '').toLowerCase()] || 'none';
};
const normalizeForm = (f: any) => ({
  ...f,
  authentication: normalizeAuth(f.authentication),
  parameters: (f.parameters || []).map((p: any) => ({
    ...p,
    type: normalizeType(p.type),
    location: normalizeLocation(p.location),
  })),
});

const errClass = (hasErr: boolean) =>
  hasErr ? 'border-destructive focus-visible:ring-destructive' : '';


const limitToast = (id: string, msg: string) => toast.error(msg, { id });


const clip = (value: string, max: number, id: string, label: string) => {
  if (value.length > max) {
    limitToast(id, `${label} can be max ${max} characters`);
    return value.slice(0, max);
  }
  return value;
};


const MobileLabel = ({ text, info }: { text: string; info: string }) => (
  <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground sm:hidden">
    {text}
    <HeadingInfo text={info} />
  </div>
);

const getNumberError = (value: any, min: number, max: number, label: string) => {
  const s = String(value ?? '').trim();
  if (s === '') return `${label} is required`;
  const n = Number(s);
  if (!Number.isInteger(n) || n < min || n > max) {
    return `${label} must be a whole number between ${min} and ${max}`;
  }
  return '';
};

export default function CustomApiFunctionDialog({ open, onOpenChange, initial, onSave }: any) {
  const [form, setForm] = useState(emptyForm());
  const [showErrors, setShowErrors] = useState(false);

  const descLimit = getAiAgentDescriptionCharLimit();

  useEffect(() => {
    if (open) {
      setShowErrors(false);
      setForm(normalizeForm(initial ? { ...emptyForm(), ...initial } : emptyForm()));
    }
  }, [open, initial]);

  const patch = (fields: any) => setForm((f: any) => ({ ...f, ...fields }));

  // ---------- Parameters ----------
  const addParameter = () => {
    if (form.parameters.length >= API_PARAMS_MAX_ITEMS) {
      toast.error(`You can add up to ${API_PARAMS_MAX_ITEMS} parameters per API`);
      return;
    }
    patch({ parameters: [...form.parameters, emptyParam()] });
  };

  const updateParameter = (id: string, fields: any) =>
    patch({ parameters: form.parameters.map((p: any) => (p.id === id ? { ...p, ...fields } : p)) });

  const removeParameter = (id: string) =>
    patch({ parameters: form.parameters.filter((p: any) => p.id !== id) });

  // ---------- Headers ----------
  const addHeader = () => {
    if (form.headers.length >= HEADERS_MAX_ITEMS) {
      toast.error(`You can add up to ${HEADERS_MAX_ITEMS} headers`);
      return;
    }
    patch({ headers: [...form.headers, emptyHeader()] });
  };

  const updateHeader = (id: string, fields: any) =>
    patch({ headers: form.headers.map((h: any) => (h.id === id ? { ...h, ...fields } : h)) });

  const removeHeader = (id: string) =>
    patch({ headers: form.headers.filter((h: any) => h.id !== id) });

  // ---------- cURL ----------
  const handleUrlChange = (raw: string) => {
    const value = clip(raw, URL_MAX, 'url-max', 'Endpoint URL');
    patch({ url: value });
    if (!looksLikeCurl(value)) return;

    const parsed = parseCurl(value);
    if (!parsed.url) {
      toast.warning('Could not parse that cURL command');
      return;
    }

    const method = HTTP_METHODS.includes(String(parsed.method || '').toUpperCase())
      ? String(parsed.method).toUpperCase()
      : 'GET';

    const allHeaders = Object.entries(parsed.headers || {});
    if (allHeaders.length > HEADERS_MAX_ITEMS) {
      toast.warning(`Only the first ${HEADERS_MAX_ITEMS} headers were imported (max ${HEADERS_MAX_ITEMS})`);
    }
    const headerArray = allHeaders.slice(0, HEADERS_MAX_ITEMS).map(([key, val]) => ({
      ...emptyHeader(),
      key: String(key).slice(0, HEADER_KEY_MAX),
      value: String(val).slice(0, HEADER_VALUE_MAX),
    }));

    let bodyParams: any[] = [];
    if (parsed.body && method !== 'GET') {
      try {
        const bodyObj = JSON.parse(parsed.body);
        bodyParams = Object.entries(bodyObj).map(([name, val]) => ({
          ...emptyParam(),
          name: String(name).slice(0, PARAM_NAME_MAX),
          type: normalizeType(guessParamType(val)),
          location: 'body',
        }));
      } catch {
        const paramsFromForm = new URLSearchParams(parsed.body);
        paramsFromForm.forEach((_val, name) => {
          bodyParams.push({
            ...emptyParam(),
            name: String(name).slice(0, PARAM_NAME_MAX),
            type: 'string',
            location: 'body',
          });
        });
      }
    }

    setForm((f: any) => {
      const kept = f.parameters.filter((p: any) => p.location !== 'body' || !bodyParams.length);
      const room = Math.max(0, API_PARAMS_MAX_ITEMS - kept.length);
      if (bodyParams.length > room) {
        toast.warning(`Only ${room} body parameter(s) imported (max ${API_PARAMS_MAX_ITEMS} parameters)`);
      }
      return {
        ...f,
        url: parsed.url.slice(0, URL_MAX),
        method,
        headers: headerArray.length ? headerArray : f.headers,
        parameters: [...kept, ...bodyParams.slice(0, room)],
      };
    });
    toast.success(
      'Filled Method, URL, Headers' + (bodyParams.length ? ' and Body parameters' : '') + ' from cURL',
    );
  };

  // ---------- Validation ----------
  const isNameValid = form.name === '' || /^[a-z0-9_]+$/.test(form.name);

  const nameCounts: Record<string, number> = {};
  form.parameters.forEach((p: any) => {
    const n = p.name.trim();
    if (n) nameCounts[n] = (nameCounts[n] || 0) + 1;
  });

  const headerKeyCounts: Record<string, number> = {};
  form.headers.forEach((h: any) => {
    const k = h.key.trim().toLowerCase();
    if (k) headerKeyCounts[k] = (headerKeyCounts[k] || 0) + 1;
  });

  const getParamErrors = (p: any) => {
    const e: any = {};
    const n = p.name.trim();
    if (!n) e.name = 'Name is required';
    else if (n.length > PARAM_NAME_MAX) e.name = `Name can be max ${PARAM_NAME_MAX} characters`;
    else if (nameCounts[n] > 1) e.name = 'Duplicate parameter name';

    if (!p.description.trim()) e.description = 'Description is required';
    else if (p.description.length > descLimit)
      e.description = `Description can be max ${descLimit} characters`;

    if (!PARAM_TYPES.includes(p.type)) e.type = 'Select a valid type';
    if (!PARAM_LOCATIONS.some((l) => l.value === p.location)) e.location = 'Select a valid location';
    return e;
  };

  const getHeaderErrors = (h: any) => {
    const e: any = {};
    const k = h.key.trim();
    if (!k) e.key = 'Key is required';
    else if (k.length > HEADER_KEY_MAX) e.key = `Key can be max ${HEADER_KEY_MAX} characters`;
    else if (headerKeyCounts[k.toLowerCase()] > 1) e.key = 'Duplicate header key';
    if (!h.value.trim()) e.value = 'Value is required';
    else if (h.value.length > HEADER_VALUE_MAX)
      e.value = `Value can be max ${HEADER_VALUE_MAX} characters`;
    return e;
  };

  const timeoutError = getNumberError(form.timeout, TIMEOUT_MIN, TIMEOUT_MAX, 'Timeout');
  const retryError = getNumberError(form.retryAttempts, RETRY_MIN, RETRY_MAX, 'Retry attempts');


  const handleSave = () => {
    setShowErrors(true);

    if (!form.name.trim()) {
      toast.error('Function Name is required');
      return;
    }
    if (!isNameValid) {
      toast.error('Function Name must use lowercase letters, numbers and underscores only');
      return;
    }
    if (form.name.length > FN_NAME_MAX) {
      toast.error(`Function Name can be max ${FN_NAME_MAX} characters`);
      return;
    }
    if (!form.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!form.method) {
      toast.error('HTTP Method is required');
      return;
    }
    if (!form.url.trim()) {
      toast.error('Endpoint URL is required');
      return;
    }


    for (let i = 0; i < form.parameters.length; i++) {
      const errs = getParamErrors(form.parameters[i]);
      const first = errs.name || errs.description || errs.type || errs.location;
      if (first) {
        toast.error(`Parameter ${i + 1}: ${first}`);
        return;
      }
    }
    for (let i = 0; i < form.headers.length; i++) {
      const errs = getHeaderErrors(form.headers[i]);
      const first = errs.key || errs.value;
      if (first) {
        toast.error(`Header ${i + 1}: ${first}`);
        return;
      }
    }
    if (timeoutError) {
      toast.error(timeoutError);
      return;
    }
    if (retryError) {
      toast.error(retryError);
      return;
    }

    onSave({ ...form, id: initial?.id || genId() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit Custom API Function' : 'Add Custom API Function'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Function Name */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="fn-name">
                Function Name
              </Label>
              <HeadingInfo text={INFO.fnName} />
            </div>
            <Input
              id="fn-name"
              placeholder="e.g. get_order_status"
              value={form.name}
              onChange={(e) => {
                const raw = e.target.value;
                const cleaned = raw.toLowerCase().replace(/[^a-z0-9_]/g, '');
                if (cleaned !== raw.toLowerCase()) {
                  limitToast(
                    'fn-name-snake',
                    'Function name must be snake_case (lowercase letters, numbers and underscores only)',
                  );
                }
                patch({ name: clip(cleaned, FN_NAME_MAX, 'fn-name-max', 'Function name') });
              }}
              className={errClass(!isNameValid || form.name.length >= FN_NAME_MAX)}
            />
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-destructive">
                {!isNameValid && form.name.length > 0
                  ? 'Name must be in snake_case (lowercase letters, numbers, and underscores only).'
                  : form.name.length >= FN_NAME_MAX
                    ? `Max ${FN_NAME_MAX} characters reached`
                    : ''}
              </p>
              <span className="shrink-0 text-xs text-muted-foreground">
                {form.name.length}/{FN_NAME_MAX}
              </span>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="fn-desc">
                Description
              </Label>
              <HeadingInfo text={`Tells the agent what this API does and when to use it.`} />
            </div>
            <Textarea
              id="fn-desc"
              rows={2}
              placeholder="Clear one-sentence description of what it does, e.g. Fetch the current status of a customer's order by order ID."
              value={form.description}
              onChange={(e) =>
                patch({ description: clip(e.target.value, descLimit, 'fn-desc-max', 'Description') })
              }
              className={errClass(form.description.length >= descLimit)}
            />
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs text-destructive">
                {form.description.length >= descLimit ? `Max ${descLimit} characters reached` : ''}
              </p>
              <span className="shrink-0 text-xs text-muted-foreground">
                {(form.description || '').length}/{descLimit}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr]">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                HTTP Method
                <HeadingInfo text="The HTTP method used for the request." />
              </Label>
              <Select value={form.method} onValueChange={(v) => patch({ method: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HTTP_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="fn-url" className="flex items-center gap-1.5">
                Endpoint URL
                <HeadingInfo
                  text={`The endpoint to call. Use {placeholders} for path params, e.g. /orders/{order_id}.`}
                />
                <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <Wand2 className="h-3 w-3" />
                  paste a cURL to auto-fill
                </span>
              </Label>
              <Textarea
                id="fn-url"
                rows={2}
                placeholder="https://api.example.com/orders/{order_id}  —  or paste a full cURL command"
                value={form.url}
                onChange={(e) => handleUrlChange(e.target.value)}
                className={`font-mono text-xs ${errClass(form.url.length >= URL_MAX)}`}
              />
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-destructive">
                  {form.url.length >= URL_MAX ? `Max ${URL_MAX} characters reached` : ''}
                </p>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {form.url.length}/{URL_MAX}
                </span>
              </div>
            </div>
          </div>

          {/* ---------------- Parameters ---------------- */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                Parameters
                <span className="text-xs font-normal text-muted-foreground">
                  ({form.parameters.length}/{API_PARAMS_MAX_ITEMS})
                </span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addParameter}
                disabled={form.parameters.length >= API_PARAMS_MAX_ITEMS}
              >
                <Plus className="h-4 w-4" />
                Add Parameter
              </Button>
            </div>

            {form.parameters.length === 0 ? (
              <p className="text-xs text-muted-foreground">No parameters added.</p>
            ) : (
              <div className="space-y-3 rounded-md border p-3">
                {/* Desktop column headers with info icons */}
                <div className="hidden grid-cols-[1fr_0.9fr_0.9fr_1.4fr_auto_auto] items-center gap-2 text-xs font-medium text-muted-foreground sm:grid">
                  <span className="flex items-center gap-1.5">
                    Name
                    <HeadingInfo text={INFO.paramName} />
                  </span>
                  <span className="flex items-center gap-1.5">
                    Type
                    <HeadingInfo text={INFO.type} />
                  </span>
                  <span className="flex items-center gap-1.5">
                    Location
                    <HeadingInfo text={INFO.location} />
                  </span>
                  <span className="flex items-center gap-1.5">
                    Description
                    <HeadingInfo text={INFO.paramDesc(descLimit)} />
                  </span>
                  <span className="flex items-center gap-1.5">
                    Required
                    <HeadingInfo text={INFO.required} />
                  </span>
                  <span />
                </div>

                {form.parameters.map((p: any) => {
                  const errs = showErrors ? getParamErrors(p) : {};
                  const nameAtLimit = p.name.length >= PARAM_NAME_MAX;
                  const descAtLimit = p.description.length >= descLimit;
                  return (
                    <div key={p.id} className="space-y-1">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_0.9fr_0.9fr_1.4fr_auto_auto] sm:items-start">
                        {/* Name */}
                        <div>
                          <MobileLabel text="Name" info={INFO.paramName} />
                          <Input
                            placeholder="e.g. order_id"
                            value={p.name}
                            onChange={(e) =>
                              updateParameter(p.id, {
                                name: clip(e.target.value, PARAM_NAME_MAX, `pname-${p.id}`, 'Parameter name'),
                              })
                            }
                            className={errClass(!!errs.name || nameAtLimit)}
                          />
                          <div className="mt-1 flex items-start justify-between gap-2">
                            <p className="text-xs text-destructive">
                              {errs.name || (nameAtLimit ? `Max ${PARAM_NAME_MAX} characters reached` : '')}
                            </p>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {p.name.length}/{PARAM_NAME_MAX}
                            </span>
                          </div>
                        </div>

                        {/* Type */}
                        <div>
                          <MobileLabel text="Type" info={INFO.type} />
                          <Select value={p.type} onValueChange={(v) => updateParameter(p.id, { type: v })}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PARAM_TYPES.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errs.type && <p className="mt-1 text-xs text-destructive">{errs.type}</p>}
                        </div>

                        {/* Location */}
                        <div>
                          <MobileLabel text="Location" info={INFO.location} />
                          <Select
                            value={p.location}
                            onValueChange={(v) => updateParameter(p.id, { location: v })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PARAM_LOCATIONS.map((l) => (
                                <SelectItem key={l.value} value={l.value}>
                                  {l.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errs.location && (
                            <p className="mt-1 text-xs text-destructive">{errs.location}</p>
                          )}
                        </div>

                        {/* Description */}
                        <div>
                          <MobileLabel text="Description" info={INFO.paramDesc(descLimit)} />
                          <Input
                            placeholder="Description for LLM"
                            value={p.description}
                            onChange={(e) =>
                              updateParameter(p.id, {
                                description: clip(
                                  e.target.value,
                                  descLimit,
                                  `pdesc-${p.id}`,
                                  'Parameter description',
                                ),
                              })
                            }
                            className={errClass(!!errs.description || descAtLimit)}
                          />
                          <div className="mt-1 flex items-start justify-between gap-2">
                            <p className="text-xs text-destructive">
                              {errs.description || (descAtLimit ? `Max ${descLimit} characters reached` : '')}
                            </p>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {p.description.length}/{descLimit}
                            </span>
                          </div>
                        </div>

                        {/* Required */}
                        <div>
                          <MobileLabel text="Required" info={INFO.required} />
                          <div className="flex h-9 items-center justify-center">
                            <Checkbox
                              checked={p.required}
                              onCheckedChange={(v) => updateParameter(p.id, { required: !!v })}
                            />
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 shrink-0 text-destructive hover:text-destructive"
                          onClick={() => removeParameter(p.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---------------- Headers ---------------- */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5">
                Headers (Optional)
                <HeadingInfo text={INFO.headers} />
                <span className="text-xs font-normal text-muted-foreground">
                  ({form.headers.length}/{HEADERS_MAX_ITEMS})
                </span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addHeader}
                disabled={form.headers.length >= HEADERS_MAX_ITEMS}
              >
                <Plus className="h-4 w-4" />
                Add Header
              </Button>
            </div>

            {form.headers.length === 0 ? (
              <p className="text-xs text-muted-foreground">No headers added.</p>
            ) : (
              <div className="space-y-3 rounded-md border p-3">
                {form.headers.map((h: any) => {
                  const errs = showErrors ? getHeaderErrors(h) : {};
                  const keyAtLimit = h.key.length >= HEADER_KEY_MAX;
                  const valAtLimit = h.value.length >= HEADER_VALUE_MAX;
                  return (
                    <div
                      key={h.id}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.4fr_auto] sm:items-start"
                    >
                      <div>
                        <Input
                          placeholder="Key (e.g. Content-Type)"
                          value={h.key}
                          onChange={(e) =>
                            updateHeader(h.id, {
                              key: clip(e.target.value, HEADER_KEY_MAX, `hkey-${h.id}`, 'Header key'),
                            })
                          }
                          className={errClass(!!errs.key || keyAtLimit)}
                        />
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <p className="text-xs text-destructive">
                            {errs.key || (keyAtLimit ? `Max ${HEADER_KEY_MAX} characters reached` : '')}
                          </p>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {h.key.length}/{HEADER_KEY_MAX}
                          </span>
                        </div>
                      </div>
                      <div>
                        <Input
                          placeholder="Value"
                          value={h.value}
                          onChange={(e) =>
                            updateHeader(h.id, {
                              value: clip(e.target.value, HEADER_VALUE_MAX, `hval-${h.id}`, 'Header value'),
                            })
                          }
                          className={errClass(!!errs.value || valAtLimit)}
                        />
                        <div className="mt-1 flex items-start justify-between gap-2">
                          <p className="text-xs text-destructive">
                            {errs.value ||
                              (valAtLimit ? `Max ${HEADER_VALUE_MAX} characters reached` : '')}
                          </p>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {h.value.length}/{HEADER_VALUE_MAX}
                          </span>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0 text-destructive hover:text-destructive"
                        onClick={() => removeHeader(h.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ---------------- Settings ---------------- */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                Authentication
                <HeadingInfo text={INFO.auth} />
              </Label>
              <Select value={form.authentication} onValueChange={(v) => patch({ authentication: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTH_TYPES.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                Timeout (seconds)
                <HeadingInfo text={INFO.timeout} />
              </Label>
              <Input
                type="number"
                min={TIMEOUT_MIN}
                max={TIMEOUT_MAX}
                value={form.timeout}
                onChange={(e) => {
                  const v = e.target.value;
                  patch({ timeout: v });
                  if (v !== '' && getNumberError(v, TIMEOUT_MIN, TIMEOUT_MAX, 'Timeout')) {
                    limitToast('timeout-range', `Timeout must be between ${TIMEOUT_MIN} and ${TIMEOUT_MAX} seconds`);
                  }
                }}
                className={errClass(!!timeoutError && (showErrors || form.timeout !== ''))}
              />
              {timeoutError && (showErrors || form.timeout !== '') && (
                <p className="text-xs text-destructive">{timeoutError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                Retry attempts
                <HeadingInfo text={INFO.retry} />
              </Label>
              <Input
                type="number"
                min={RETRY_MIN}
                max={RETRY_MAX}
                value={form.retryAttempts}
                onChange={(e) => {
                  const v = e.target.value;
                  patch({ retryAttempts: v });
                  if (v !== '' && getNumberError(v, RETRY_MIN, RETRY_MAX, 'Retry attempts')) {
                    limitToast('retry-range', `Retry attempts must be between ${RETRY_MIN} and ${RETRY_MAX}`);
                  }
                }}
                className={errClass(!!retryError && (showErrors || form.retryAttempts !== ''))}
              />
              {retryError && (showErrors || form.retryAttempts !== '') && (
                <p className="text-xs text-destructive">{retryError}</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Function
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}